import httpClient from 'utility/httpClient.util'
import {
  REPORT_IMAGE_MAX_SIZE,
  REPORT_PDF_MAX_SIZE,
  buildPdfResourceItem,
  getAttachmentFileName,
  getAttachmentId,
  uploadReportStaticFile,
} from './resourceUpload'
import {
  getReportResourceItems,
  normalizePortableReportResources,
} from './reportResource.util'

const IMAGE_DATA_URL_PATTERN = /^data:image\/[a-z0-9.+-]+;base64,/i
const PDF_DATA_URL_PATTERN = /^data:application\/pdf;base64,/i
const RAW_BASE64_PATTERN = /^[A-Za-z0-9+/]+={0,2}$/
const PORTABLE_ASSET_VERSION = 1

const cloneReportData = (value) => JSON.parse(JSON.stringify(value || {}))

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const isDataUrlImage = (value = '') => IMAGE_DATA_URL_PATTERN.test(value)
const isDataUrlPdf = (value = '') => PDF_DATA_URL_PATTERN.test(value)

const getImageMimeType = (value = '', fallback = 'image/png') => {
  const dataUrlMatch = `${value || ''}`.match(/^data:([^;]+);base64,?/i)
  if (dataUrlMatch?.[1]) return dataUrlMatch[1]

  const lowerValue = `${value || ''}`.toLowerCase()
  if (lowerValue.includes('jpeg') || lowerValue.endsWith('.jpg')) {
    return 'image/jpeg'
  }
  if (lowerValue.includes('webp') || lowerValue.endsWith('.webp')) {
    return 'image/webp'
  }
  if (lowerValue.includes('gif') || lowerValue.endsWith('.gif')) {
    return 'image/gif'
  }

  return fallback
}

const getImageExtension = (mimeType = 'image/png') => {
  if (mimeType.includes('jpeg') || mimeType.includes('jpg')) return 'jpg'
  if (mimeType.includes('webp')) return 'webp'
  if (mimeType.includes('gif')) return 'gif'
  return 'png'
}

const getPdfSourceValue = (value) => {
  if (typeof value !== 'string') return ''

  const text = value.trim()
  if (!text) return ''
  if (isDataUrlPdf(text)) return text
  if (text.length > 100 && RAW_BASE64_PATTERN.test(text)) {
    return `data:application/pdf;base64,${text}`
  }

  return ''
}

const getPortablePdfSource = (value) => {
  if (!value) return ''

  if (typeof value === 'string') {
    return getPdfSourceValue(value)
  }

  if (isPlainObject(value)) {
    const sourceKeys = ['base64', 'data', 'value', 'src', 'url']

    for (const key of sourceKeys) {
      const source = getPortablePdfSource(value[key])
      if (source) return source
    }
  }

  return ''
}

const getImageSourceValue = (value, mimeType) => {
  if (typeof value !== 'string') return ''

  const text = value.trim()
  if (!text) return ''
  if (isDataUrlImage(text)) return text
  if (text.length > 100 && RAW_BASE64_PATTERN.test(text)) {
    return `data:${mimeType || 'image/png'};base64,${text}`
  }

  return ''
}

const getPortableImageSource = (value, fallbackMimeType = 'image/png') => {
  if (!value) return ''

  if (typeof value === 'string') {
    return getImageSourceValue(value, fallbackMimeType)
  }

  if (isPlainObject(value)) {
    const mimeType = value.mimeType || value.contentType || fallbackMimeType
    const sourceKeys = ['base64', 'data', 'value', 'src', 'url']

    for (const key of sourceKeys) {
      const source = getPortableImageSource(value[key], mimeType)
      if (source) return source
    }
  }

  return ''
}

const readBlobAsDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = (error) => reject(error)
    reader.readAsDataURL(blob)
  })

const fetchAttachmentAsDataUrl = async (attachment) => {
  const attachmentId = getAttachmentId(attachment)
  if (!attachmentId) return ''

  const response = await httpClient.get(
    `/media/attachment/originalFile/${attachmentId}`,
    { responseType: 'blob' },
  )

  return readBlobAsDataUrl(response?.data)
}

const fetchAttachmentImageAsDataUrl = fetchAttachmentAsDataUrl

const getImageFileName = (item = {}, image = {}, mimeType = 'image/png') => {
  const fileName = getAttachmentFileName(
    image,
    item?.value || item?.title || 'report-image',
  )
  const hasExtension = /\.[a-z0-9]+$/i.test(fileName)

  return hasExtension
    ? fileName
    : `${fileName}.${getImageExtension(mimeType)}`
}

const buildPortableImagePayload = (item = {}, image = {}, dataUrl = '') => {
  if (!dataUrl) return null

  const mimeType = getImageMimeType(dataUrl, image?.mimeType || image?.contentType)

  return {
    __portableImage: true,
    base64: dataUrl,
    fileName: getImageFileName(item, image, mimeType),
    mimeType,
    width: image?.width || item?.image?.width || item?.data?.width || '',
    height: image?.height || item?.image?.height || item?.data?.height || '',
  }
}

const preparePortableExportImage = async (item = {}) => {
  const image = item?.image || {}
  const existingDataUrl =
    getPortableImageSource(image) || getPortableImageSource(item?.value)
  const dataUrl =
    existingDataUrl ||
    (getAttachmentId(image) ? await fetchAttachmentImageAsDataUrl(image) : '')

  return buildPortableImagePayload(item, image, dataUrl)
}

const getPdfFileName = (item = {}, pdf = {}) => {
  const fileName = getAttachmentFileName(
    pdf,
    item?.value || item?.title || 'report.pdf',
  )

  return /\.pdf$/i.test(fileName) ? fileName : `${fileName}.pdf`
}

const getDataUrlSize = (dataUrl = '') => {
  const base64Data = `${dataUrl || ''}`.split(',')[1] || ''
  if (!base64Data) return 0

  const padding = (base64Data.match(/=*$/) || [''])[0].length
  return Math.max(Math.floor((base64Data.length * 3) / 4) - padding, 0)
}

const buildPortablePdfPayload = (
  item = {},
  pdf = {},
  dataUrl = '',
  layoutData = {},
) => {
  if (!dataUrl) return null
  if (getDataUrlSize(dataUrl) > REPORT_PDF_MAX_SIZE) {
    throw new Error('PDF ต้องมีขนาดไม่เกิน 10 MB')
  }

  return {
    __portableAsset: true,
    version: PORTABLE_ASSET_VERSION,
    kind: 'pdf',
    encoding: 'data-url',
    base64: dataUrl,
    fileName: getPdfFileName(item, pdf),
    mimeType: 'application/pdf',
    sourceAttachmentId:
      getAttachmentId(item) || getAttachmentId(pdf) || '',
    sha256: layoutData?.pdfTemplate?.sha256 || '',
  }
}

const preparePortableExportPdf = async (item = {}, layoutData = {}) => {
  const pdf = item?.pdf || {}
  const existingDataUrl =
    getPortablePdfSource(item?.portableAsset) ||
    getPortablePdfSource(pdf) ||
    getPortablePdfSource(item?.data) ||
    getPortablePdfSource(item?.value)
  const attachment = getAttachmentId(pdf) ? pdf : item
  const dataUrl =
    existingDataUrl ||
    (getAttachmentId(attachment)
      ? await fetchAttachmentAsDataUrl(attachment)
      : '')

  return buildPortablePdfPayload(item, pdf, dataUrl, layoutData)
}

export const buildPortableReportExportData = async (reportData = {}) => {
  const nextReportData = cloneReportData(reportData)
  normalizePortableReportResources(nextReportData)
  const invalidResourceItems = getReportResourceItems(nextReportData).filter(
    (item) => !item.resourceCode,
  )
  if (invalidResourceItems.length) {
    throw new Error('Report Resource ต้องมี report code ก่อน Export')
  }

  const printData = nextReportData?.webviewData?.printData || {}
  const layoutData = nextReportData?.webviewData?.layoutData || {}
  const printDataKeys = Object.keys(printData)

  await Promise.all(
    printDataKeys.map(async (key) => {
      const item = printData[key]
      if (item?.type === 'image') {
        const portableImage = await preparePortableExportImage(item)
        if (!portableImage) return

        item.image = portableImage
        item.value = item.value || portableImage.fileName
        delete item.data
        delete item.previewUrl
        return
      }

      if (item?.type !== 'pdf') return

      const portablePdf = await preparePortableExportPdf(item, layoutData)
      if (!portablePdf) return

      item.portableAsset = portablePdf
      delete item.data
      delete item.previewUrl
      if (isPlainObject(item.pdf)) {
        delete item.pdf.previewUrl
      }
    }),
  )

  return nextReportData
}

const dataUrlToFile = (dataUrl, fileName) => {
  const [meta, base64Data = ''] = `${dataUrl || ''}`.split(',')
  const mimeType = getImageMimeType(meta)
  const byteCharacters = atob(base64Data)
  const byteArrays = []

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512)
    const byteNumbers = new Array(slice.length)

    for (let index = 0; index < slice.length; index++) {
      byteNumbers[index] = slice.charCodeAt(index)
    }

    byteArrays.push(new Uint8Array(byteNumbers))
  }

  return new File(byteArrays, fileName, { type: mimeType })
}

const getPortableImportImage = (item = {}) => {
  const portableAsset =
    item?.portableAsset?.kind === 'image' ? item.portableAsset : {}
  const image = item?.image || portableAsset
  const mimeType = image?.mimeType || image?.contentType || 'image/png'
  const dataUrl =
    getPortableImageSource(portableAsset, mimeType) ||
    getPortableImageSource(image, mimeType) ||
    getPortableImageSource(item?.value, mimeType)

  if (!dataUrl) return null

  return {
    dataUrl,
    fileName:
      portableAsset?.fileName ||
      image?.fileName ||
      getImageFileName(item, image, mimeType),
    width: portableAsset?.width || image?.width || item?.data?.width || '',
    height: portableAsset?.height || image?.height || item?.data?.height || '',
  }
}

const getPortableImportPdf = (item = {}) => {
  const portableAsset =
    item?.portableAsset?.kind === 'pdf' ? item.portableAsset : {}
  const pdf = item?.pdf || {}
  const dataUrl =
    getPortablePdfSource(portableAsset) ||
    getPortablePdfSource(pdf) ||
    getPortablePdfSource(item?.data) ||
    getPortablePdfSource(item?.value)

  if (!dataUrl) return null

  return {
    dataUrl,
    fileName:
      portableAsset?.fileName || pdf?.fileName || getPdfFileName(item, pdf),
    sourceAttachmentId:
      portableAsset?.sourceAttachmentId ||
      getAttachmentId(item) ||
      getAttachmentId(pdf) ||
      '',
  }
}

const updatePdfTemplateAttachmentId = (
  webviewData,
  sourceAttachmentIds,
  attachmentId,
) => {
  const pdfTemplate = webviewData?.layoutData?.pdfTemplate
  if (!pdfTemplate || !attachmentId) return

  const templateAttachmentId = `${pdfTemplate.attachmentId || ''}`
  const pdfItemCount = Object.values(webviewData?.printData || {}).filter(
    (item) => item?.type === 'pdf',
  ).length
  const shouldUpdate =
    !templateAttachmentId ||
    sourceAttachmentIds.includes(templateAttachmentId) ||
    pdfItemCount === 1

  if (shouldUpdate) {
    pdfTemplate.attachmentId = attachmentId
  }
}

export const uploadPortableReportAssets = async (reportData = {}) => {
  const nextReportData = cloneReportData(reportData)
  const webviewData = nextReportData.webviewData || nextReportData
  const printData = webviewData?.printData || {}
  const printDataKeys = Object.keys(printData)

  for (const key of printDataKeys) {
    const item = printData[key]
    if (item?.type === 'image') {
      const portableImage = getPortableImportImage(item)
      if (!portableImage) continue

      const file = dataUrlToFile(
        portableImage.dataUrl,
        portableImage.fileName,
      )
      if (file.size > REPORT_IMAGE_MAX_SIZE) {
        throw new Error('รูปภาพต้องมีขนาดไม่เกิน 2 MB')
      }
      const attachment = await uploadReportStaticFile(
        file,
        portableImage.fileName,
      )
      const itemValueIsImageSource = Boolean(
        getPortableImageSource(
          item.value,
          getImageMimeType(portableImage.dataUrl),
        ),
      )

      item.image = {
        ...attachment,
        width: portableImage.width,
        height: portableImage.height,
      }
      item.value = itemValueIsImageSource
        ? portableImage.fileName
        : item.value || portableImage.fileName
      delete item.portableAsset
      delete item.data
      delete item.previewUrl
      continue
    }

    if (item?.type !== 'pdf') continue

    const portablePdf = getPortableImportPdf(item)
    if (!portablePdf) continue

    const file = dataUrlToFile(portablePdf.dataUrl, portablePdf.fileName)
    if (file.size > REPORT_PDF_MAX_SIZE) {
      throw new Error('PDF ต้องมีขนาดไม่เกิน 10 MB')
    }

    const sourceAttachmentIds = [
      portablePdf.sourceAttachmentId,
      getAttachmentId(item),
      getAttachmentId(item?.pdf),
    ]
      .filter(Boolean)
      .map(String)
    const attachment = await uploadReportStaticFile(
      file,
      portablePdf.fileName,
    )
    const nextItem = buildPdfResourceItem(
      item,
      attachment,
      portablePdf.fileName,
    )

    delete nextItem.portableAsset
    delete nextItem.data
    printData[key] = nextItem
    updatePdfTemplateAttachmentId(
      webviewData,
      sourceAttachmentIds,
      getAttachmentId(attachment),
    )
  }

  return nextReportData
}

export const uploadPortableReportImages = uploadPortableReportAssets
